import { toast } from "sonner";
import { z } from "zod";

export const BaseBreakDown = z.object({
  item: z.string(),
  position: z.coerce.number().min(0),
  description: z.string(),
  unit: z.string(),
  quantity: z.coerce
    .number({ message: "Quantity cannot be empty" })
    .min(1, { message: "Quantity cannot be 0" }),
  lastTaskProgress: z.coerce
    .number({
      message: "Last Task Progress Is Required",
    })
    .min(0)
    .max(100),
  newProgress: z.coerce
    .number({ message: "New Progress cannot be empty" })
    .max(100),
  change: z.coerce.number().max(100).default(0),
});

const TaskBreakDown = BaseBreakDown.superRefine((data, ctx) => {
  if ((data?.newProgress || 0) > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "New Progress cannot be greater than 100",
      path: ["newProgress"],
    });
  }
});

export const TaskBreakDownDetails = TaskBreakDown.and(
  z.union([
    z.object({
      isCustom: z.literal(true),
      customDetails: z.array(TaskBreakDown),
    }),
    z.object({
      isCustom: z.literal(false),
    }),
  ]),
);

export const BaseBreakDownData = z.object({
  name: z.string(),
  total: z.coerce.number({ message: "Total cannot be empty" }).max(100),
  position: z.coerce.number({ message: "Position is invalid" }),
  details: z.array(TaskBreakDownDetails),
});

export const TaskBreakDownData = BaseBreakDownData.superRefine((data, ctx) => {
  if (data.total > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Total cannot be greater than 100",
    });
  }
  if (data.total < 0 || data.total === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Task Total cannot be 0 or null",
    });
  }
});

export const TaskBreakdownSchema = z.object({
  data: z.array(TaskBreakDownData),
});

export type ITaskBreakDownData = z.infer<typeof TaskBreakDownData>;
export type ITaskBreakDownSchema = z.infer<typeof TaskBreakdownSchema>;

// claims

export const ClaimBreakdown = BaseBreakDown.omit({ newProgress: true })
  .extend({
    claimed: z.coerce.number().min(0).max(100),
    newClaim: z.coerce
      .number({ message: "New Claim cannot be empty" })
      .max(100)
      .default(0),
  })
  .superRefine((data, ctx) => {
    // if (data.claimed > data.newClaim) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     message: "Claimed value cannot be greater than New Claim",
    //   });
    // }
    if (data?.newClaim > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Claimed value cannot be greater than 100",
      });
    }
  });

export const ClaimBreakdownDetail = ClaimBreakdown.and(
  z.union([
    z.object({
      isCustom: z.literal(true),
      customDetails: z.array(ClaimBreakdown),
    }),
    z.object({
      isCustom: z.literal(false),
    }),
  ]),
);

export const ClaimBreakdownData = BaseBreakDownData.merge(
  z.object({
    details: z.array(ClaimBreakdownDetail),
  }),
).superRefine((data, ctx) => {
  if (data.total > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Total cannot be greater than 100",
    });
  }
  if (data.total === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Total cannot be 0 or null",
      path: ["total"],
    });
    toast.info(`Claim No ${data.position + 1}'s  Total cannot be empty`);
  }
});

export const ClaimBreakdownSchema = z.object({
  data: z.array(ClaimBreakdownData),
});

export type IClaimBreakDownData = z.infer<typeof ClaimBreakdownData>;
export type IClaimBreakdownSchema = z.infer<typeof ClaimBreakdownSchema>;

export const PastClaimSchema = z
  .object({
    id: z.string().optional(),
    itemName: z.string(),
    progress: z.coerce.number().min(0).max(100).default(0),
    previousTotal: z.coerce.number(),
    progressValue: z.coerce.number(),
    projectProgress: z.coerce.number().min(0).max(100),
    claimedValue: z.coerce.number(),
    itemValue: z.coerce.number().min(0),
    invoiceProgress: z.coerce.number().min(0).max(100),
    thisClaim: z.coerce.number(),
    approvedClaimId: z.string().optional(),
    claimBreakDown: z.array(ClaimBreakdownData),
  })
  .superRefine((data, ctx) => {
    if (data.progress > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Progress Value cannot be greater than 100",
      });
    }
    // const previousClaimedPercentage =
    //   (data.claimedValue / data.itemValue) * 100;

    // Lower limit check
    // if (data.progress < previousClaimedPercentage) {
    //   ctx.addIssue({
    //     path: ["progress"],
    //     code: z.ZodIssueCode.custom,
    //     message:
    //       "Progress value cannot be less than previously claimed percentage",
    //   });
    // }
    // upper limit check
    // if (data.progress > data.projectProgress) {
    //   ctx.addIssue({
    //     path: ["progress"],
    //     code: z.ZodIssueCode.custom,
    //     message: "Progress value cannot be greater than project progress",
    //   });
    // }

    // if (data?.previousTotal >= data?.progress) {
    //   ctx.addIssue({
    //     path: ["progress"],
    //     code: z.ZodIssueCode.custom,
    //     message: "progress is zero or unchanged",
    //   });
    // }
  });

export type TPastClaimSchema = z.infer<typeof PastClaimSchema>;

export const PastClaimsSchemaArray = z.object({
  data: z.array(PastClaimSchema),
});
export type TPastClaimsSchemaArray = z.infer<typeof PastClaimsSchemaArray>;
